import React, { useEffect, useState } from "react";
import { TodoItem } from "@/api/types";
import { processQueue } from "@/api/queue.ts";
import { deleteItem, updateItem } from "@/api/offline.ts";
import { aiItemExpiresIn } from "@/api/voice.ts";
import { deleteStateEntry, itemsState, uiState, updateTaskCounts } from "@/states.ts";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { EllipsisIcon, GripHorizontalIcon, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils.ts";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";

export interface ItemViewProps {
	listId: string;
	item: TodoItem;
}

const ItemView: React.FC<ItemViewProps> = (props) => {
	const { item, listId } = props;

	const [aiRecent, setAiRecent] = useState(() => aiItemExpiresIn(item.id) > 0);
	useEffect(() => {
		const remaining = aiItemExpiresIn(item.id);
		if (!remaining) return;
		const timer = setTimeout(() => setAiRecent(false), remaining);
		return () => clearTimeout(timer);
	}, [item.id]);

	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
		id: props.item.id,
	});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	const handleToggleItem = (e: any) => {
		const completed = !item.completed;
		updateItem(listId, { ...item, completed }).then((l) => {
			itemsState.items[listId].forEach((i) => {
				if (i.id === item.id) {
					i.completed = completed;
				}
			});
			updateTaskCounts(listId);
		});
		processQueue();
	};

	const handleDelete = async () => {
		const confirmDelete = window.confirm("Are you sure you want to delete this item?");
		if (!confirmDelete) return;

		deleteItem(listId, item).then((l) => {
			deleteStateEntry(itemsState.items, listId, item);
			updateTaskCounts(listId);
		});
		processQueue();
	};

	const handleEdit = () => {
		uiState.editingItem = item;
	};

	return (
		<div
			className={cn("flex items-center gap-2 border m-2 p-2 rounded", {
				"opacity-50 grayscale": item.completed,
			})}
			ref={setNodeRef}
			style={item.completed ? undefined : style}
			{...attributes}
		>
			<GripHorizontalIcon
				{...(item.completed ? {} : listeners)}
				width={16}
				className={cn("touch-none opacity-20", {
					"cursor-move opacity-60 hover:opacity-100 ":
						!item.completed,
				})}
			/>
			<Checkbox
				id={`check-${item.id}`}
				checked={item.completed || false}
				onCheckedChange={handleToggleItem}
				style={
					item.color && !item.completed
						? {
								backgroundColor: `${item.color}33`,
								borderColor: item.color,
								color: item.color,
							}
						: undefined
				}
			/>
			{item.link ? (
				<a
					href={item.link}
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
					className={cn("flex-1 underline text-blue-500", {
						"line-through": item.completed,
					})}
				>
					{item.text}
				</a>
			) : (
				<label
					className={cn("flex-1 cursor-pointer", {
						"line-through": item.completed,
					})}
					htmlFor={`check-${item.id}`}
				>
					{item.text}
				</label>
			)}

			{aiRecent && (
				<Sparkles
					size={14}
					className="shrink-0 text-amber-500"
					aria-label="Created by AI"
				/>
			)}

			{!item.completed && (
				<DropdownMenu>
					<DropdownMenuTrigger>
						<EllipsisIcon
							width={16}
							className={
								"opacity-40 hover:opacity-100 cursor-pointer touch-none"
							}
						/>
					</DropdownMenuTrigger>
					<DropdownMenuContent>
						<DropdownMenuItem onClick={handleEdit}>
							Edit
						</DropdownMenuItem>
						<DropdownMenuItem onClick={handleDelete}>
							Delete
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</div>
	);
};

export { ItemView };
